import { type RefObject, useEffect } from 'react';

import type { ReadableStore } from '@frontend/viewer-core';

import type { ToolId, ViewerMode } from '../session/types';
import { selectedLabelText } from '../utils/textSelection';

export type TextSelectionDeps = {
  layerRef: RefObject<HTMLElement>;
  mode: ReadableStore<ViewerMode>;
  tools: ReadableStore<ToolId>;
  /** False while the layer is hidden, paused, or has no drawing to show. */
  isEnabled: boolean;
};

/**
 * Makes the DXF text layer's labels selectable and copyable in view mode.
 *
 * Selectability is a `data-selectable` attribute on the layer, which the layer's
 * own styles turn into `pointer-events: auto`, `user-select: text` and the I-beam
 * on each label. The layer itself stays `pointer-events: none`, so the canvas
 * between labels still pans, boxes and picks as before.
 *
 * Live only in view mode, under the select tool.
 *
 * Rotated labels need nothing extra: each is placed with a CSS `rotate()`, and
 * the browser hit-tests and highlights through the transform, so a drag along a
 * diagonal string selects its characters and the highlight turns with it.
 *
 * Copy is taken over only for a selection wholly inside the layer — see
 * `selectedLabelText` for why the browser's own text is not good enough.
 */
export const useDxfTextSelection = ({
  layerRef,
  mode,
  tools,
  isEnabled,
}: TextSelectionDeps): void => {
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) {
      return;
    }

    const sync = () => {
      const isSelectable =
        isEnabled &&
        mode.getState() === 'view' &&
        tools.getState() === 'select';
      if (isSelectable) {
        layer.dataset.selectable = '';
        return;
      }
      delete layer.dataset.selectable;

      // A highlight left on labels that can no longer be pressed could be
      // neither extended nor cleared from them.
      const selection = window.getSelection();
      if (
        selection &&
        selection.rangeCount > 0 &&
        layer.contains(selection.getRangeAt(0).commonAncestorContainer)
      ) {
        selection.removeAllRanges();
      }
    };

    const onCopy = (event: ClipboardEvent) => {
      const text = selectedLabelText(layer, window.getSelection());
      if (text === null || !event.clipboardData) {
        return;
      }
      event.preventDefault();
      event.clipboardData.setData('text/plain', text);
    };

    const unsubscribes = [mode.subscribe(sync), tools.subscribe(sync)];
    sync();
    // On the document: `copy` is dispatched at the focused element, which for a
    // selection in non-editable text is not reliably the layer.
    document.addEventListener('copy', onCopy);

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
      document.removeEventListener('copy', onCopy);
      delete layer.dataset.selectable;
    };
  }, [layerRef, mode, tools, isEnabled]);
};
